import { styled } from "@linaria/react"
import { runInAction } from "mobx"
import { observer } from "mobx-react-lite"
import { useState } from "react"

import { Badge } from "@/components/Badge/Badge.tsx"
import { Button } from "@/components/Button/Button.tsx"
import { DropdownMenu, DropdownMenuItem, DropdownMenuTrigger } from "@/components/DropdownMenu/DropdownMenu.tsx"
import { DropdownWithFilter } from "@/components/DropdownMenu/DropdownWithFilter.tsx"
import { Horizontal, Vertical } from "@/components/Flex.ts"
import { HighlightMatches } from "@/components/HighlightMatches/HighlightMatches.tsx"
import { PromptInputStyles } from "@/components/PromptInput/PromptInputStyles.ts"
import { SecondaryTextSpan } from "@/components/SecondaryTextSpan/SecondaryTextSpan.ts"
import { Tooltip } from "@/components/Tooltip/Tooltip.tsx"
import { useBatchLoader } from "@/hooks/useBatchLoader.ts"
import { useBranches } from "@/hooks/useBranches.ts"
import { BranchRegular, Icon20 } from "@/icons/Icons.ts"
import { branchDisplayName, pluralize } from "@/lib/displayUtils.ts"
import { ellipsis, focusOutline } from "@/styles/mixins.ts"
import { smallestFontSize } from "@/styles/textStyles.ts"

import type { BranchSelection } from "@/components/BranchSelection/BranchSelection.ts"
import type { PromptInputModel } from "@/components/PromptInput/PromptInputModel.ts"
import type { ProjectKey } from "@/lib/client/generated/TypeDefinitions.ts"

export const BranchSelector = observer(({ model, inputModel, projectKey, repositoryName, goToSettings }: {
  model: BranchSelection | null
  inputModel: PromptInputModel
  projectKey?: ProjectKey
  repositoryName?: string
  goToSettings: () => void
}) => {

  const [dropdownOpen, setDropdownOpen] = useState(false)

  if (!model || !projectKey || !repositoryName) {
    return (
      <Tooltip content="Branch selection is not available">
        <PromptInputStyles.FooterIconContainer>
          <BranchRegular className={Icon20} />
        </PromptInputStyles.FooterIconContainer>
      </Tooltip>
    )
  }

  const selectedBranch = model.selectedBaseBranch.get()
  const warmupInfo = model.warmupInfo.get()

  const displayName = selectedBranch ? branchDisplayName(selectedBranch.head) : ""
  const isDefault = warmupInfo && selectedBranch && branchDisplayName(warmupInfo.defaultBranch.head) === displayName
  const hasWarmup = warmupInfo?.latestWarmupBranch && selectedBranch && branchDisplayName(warmupInfo.latestWarmupBranch.head) === displayName && warmupInfo.latestWarmupBranch.ref === selectedBranch.ref

  const hasSyncErrors = model.repoSyncStatus.get()?.authError === true

  return (
    <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
      <Tooltip content={selectedBranch ? displayName : "Select branch"}>
        <DropdownMenuTrigger asChild>
          {selectedBranch ? (
            <BranchToggle error={hasSyncErrors}>
              <BranchRegular className={Icon20} />
              <BranchToggleText>
                {displayName}
              </BranchToggleText>
              {isDefault && hasWarmup && (
                <Badge kind="info" textTransform="uppercase" variant="small">warmed-up</Badge>
              )}
              {isDefault && !hasWarmup && warmupInfo?.commitDiffCount && (
                <Badge kind="success" textTransform="uppercase" variant="small">recent</Badge>
              )}
            </BranchToggle>
          ) : (
            <BranchIconWithError error={hasSyncErrors}>
              <BranchRegular className={Icon20} />
            </BranchIconWithError>
          )}
        </DropdownMenuTrigger>
      </Tooltip>
      {dropdownOpen && (
        <BranchDropdownContent
          model={model}
          inputModel={inputModel}
          projectKey={projectKey}
          repositoryName={repositoryName}
          goToSettings={goToSettings}
        />
      )}
    </DropdownMenu>
  )
})

const BranchDropdownContent = observer((props: {
  model: BranchSelection
  inputModel: PromptInputModel
  projectKey: ProjectKey
  repositoryName: string
  goToSettings: () => void
}) => {
  const [inputValue, setInputValue] = useState("")

  const items = useBatchLoader(
    useBranches,
    (batch) => batch.data,
    props.projectKey,
    props.repositoryName,
    inputValue
  )

  const warmupInfo = props.model.warmupInfo.get()
  const warmupBranch = warmupInfo?.latestWarmupBranch || null
  const defaultBranch = warmupInfo?.defaultBranch || null
  const itemsForDropdown = (() => {
    let computed = items.allLoaded ?? []

    if (inputValue === "") {
      // Filter out duplicates of warmup and default branches
      computed = computed.filter(b =>
        !(warmupBranch && branchDisplayName(b.head) === branchDisplayName(warmupBranch.head) && b.ref === warmupBranch.ref) &&
        !(defaultBranch && branchDisplayName(b.head) === branchDisplayName(defaultBranch.head) && (b.ref === defaultBranch.ref || b.ref === ""))
      )

      // Place default branch
      if (defaultBranch) {
        computed = [defaultBranch, ...computed]
      }

      // Place a warmed-up branch
      if (warmupBranch) {
        if (!(defaultBranch && branchDisplayName(defaultBranch.head) === branchDisplayName(warmupBranch.head) && defaultBranch.ref === warmupBranch.ref)) {
          computed = [warmupBranch, ...computed]
        }
      }
    }

    return { ...items, allLoaded: computed }
  })()

  return (
    <DropdownWithFilter
      items={itemsForDropdown}
      maxHeight={220}
      inputValue={inputValue}
      setInputValue={setInputValue}
      renderItem={(branch, index) => {
        const warmupInfo = props.model.warmupInfo.get()
        const displayName = branchDisplayName(branch.head)

        const isDefault = warmupInfo && branchDisplayName(warmupInfo.defaultBranch.head) === displayName
        const hasWarmup = warmupInfo?.latestWarmupBranch && branchDisplayName(warmupInfo.latestWarmupBranch.head) === displayName && warmupInfo.latestWarmupBranch.ref === branch.ref

        const hasSyncErrors = props.model.repoSyncStatus.get()?.authError === true

        const selectedBranch = props.model.selectedBaseBranch.get()

        return (
          <BranchMenuItem
            key={`${displayName}-${index}`}
            isSelected={
              (selectedBranch?.head === branch.head && selectedBranch?.ref === branch.ref) ||
              (selectedBranch === null && hasWarmup === true)
            }
            error={hasSyncErrors && isDefault === true}
            onClick={() => {
              runInAction(() => props.inputModel.setSelectedBranch(branch))
            }}
          >
            <Vertical gap="8px">
              <BranchItemContent gap="8px" alignItems="center">
                <BranchRegular className={Icon20} />
                <Tooltip content={displayName} align="start" side="bottom">
                  <BranchName>
                    <HighlightMatches text={displayName} query={inputValue} />
                  </BranchName>
                </Tooltip>
                {isDefault && (
                  <>
                    {hasSyncErrors && (
                      <>
                        <Badge kind="error" textTransform="uppercase" variant="small">fetch failed</Badge>
                      </>
                    )}
                    {!hasSyncErrors && (
                      hasWarmup ? (
                        <>
                          <Badge kind="info" textTransform="uppercase" variant="small">warmed-up</Badge>
                          <MainColorMetadata>loads faster</MainColorMetadata>
                        </>
                      ) : (
                        <>
                          <Badge kind="success" textTransform="uppercase" variant="small">recent</Badge>

                          {warmupInfo?.commitDiffCount && (
                            <SuccessColorMetadata>{warmupInfo?.commitDiffCount} new {pluralize("commit", warmupInfo?.commitDiffCount)}</SuccessColorMetadata>
                          )}
                        </>
                      )
                    )}
                  </>
                )}
              </BranchItemContent>

              {hasSyncErrors && isDefault && !hasWarmup && (
                <>
                  <ErrorDescription>Update credentials to fetch newer versions</ErrorDescription>
                  <SettingsButton kind="secondary" onClick={() => {
                    props.goToSettings()
                  }}>Open settings</SettingsButton>
                </>
              )}
            </Vertical>
          </BranchMenuItem>
        )
      }}
    />
  )
})

const BranchToggleText = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  ${ellipsis};
`

const BranchToggle = styled(Horizontal)<{ error: boolean }>`
  gap: 4px;
  align-items: center;

  max-width: min(200px, 20vw);
  min-width: 0;
  height: 24px;

  padding-left: 2px;

  cursor: pointer;

  color: ${props => props.error ? "var(--error-color-60)" : "var(--secondary-text-color)"};
  transition: color 0.2s ease-out, transform 0.1s ease-out;

  &:hover {
    color: ${props => props.error ? "var(--error-color)" : "var(--text-color)"};
  }

  &:focus-visible {
    ${focusOutline};
  }
`

const BranchIconWithError = styled(PromptInputStyles.FooterIconContainer)<{ error: boolean }>`
  color: ${props => props.error ? "var(--error-color-60)" : "var(--secondary-text-color)"};

  &:hover:not(:disabled) {
    color: ${props => props.error ? "var(--error-color)" : "var(--text-color)"};
  }
`

const BranchMenuItem = styled(DropdownMenuItem)<{ isSelected: boolean, error: boolean }>`
  padding: 8px;
  margin-bottom: 4px;

  border-radius: var(--border-radius-8);
  background-color: ${props => props.isSelected ? "var(--main-color-5)" : props.error ? "var(--error-color-10)" : "transparent"};
`

const BranchItemContent = styled(Horizontal)`
  ${ellipsis};

  border-radius: var(--border-radius-8);
`

const BranchName = styled.span`
  font-size: 14px;
  line-height: 18px;
  color: var(--text-color);
  ${ellipsis};
`

const ErrorDescription = styled.span`
  color: var(--secondary-text-color);
  ${smallestFontSize};
  ${ellipsis};
`

const SettingsButton = styled(Button)`
  align-self: flex-start;
  width: fit-content;
`

const BranchMetadata = styled(SecondaryTextSpan)`
  font-size: 9px;
  font-weight: 500;
  line-height: 14px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  margin-left: auto;
`

const SuccessColorMetadata = styled(BranchMetadata)`
  color: var(--success-color);
`

const MainColorMetadata = styled(BranchMetadata)`
  color: var(--main-color);
`
